import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getMovieRecommendations, getGenres, getSurpriseMovie } from '../services/movieService';
import { getMoviePoster, getMovieDetailsWithCast } from '../services/tmdbService'; // TMDB servisi eklendi
import { addToFavorites, removeFromFavorites, isFavorite, markAsWatched, markAsUnwatched, isWatched, registerGlobalRefresh, getFavorites, getWatchedMovies } from '../services/favoriteService';
import { getMovieTrailer } from '../services/tmdbService';
import TrailerModal from '../components/TrailerModal';
import ConfirmationModal from '../components/ConfirmationModal';
import Toast from '../components/Toast';
import { Sparkles, Film, Star, Loader, Send, Clock, Shuffle, Heart, Eye, EyeOff, Play, Search, X, Home, Globe, Smile, Frown, Zap, Coffee, Mountain } from 'lucide-react';
import ReactGA from "react-ga4";

ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);

const RecommendationPage = () => {
    const { user, updateUserPreferences } = useAuth();
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        genres: [],
        mood: '',
        contentType: 'film', // 'film' veya 'dizi'
        origin: 'yabancı' // 'yerli' veya 'yabancı'
    });
    const [lastUsedPreferences, setLastUsedPreferences] = useState(null); // Son kullanılan tercihleri sakla
    const [isLoading, setIsLoading] = useState(false);
    const [recommendations, setRecommendations] = useState(null);
    const [error, setError] = useState('');
    const [selectedMovie, setSelectedMovie] = useState(null); // Detay modal için
    // Sürpriz film state'leri
    const [surpriseMovie, setSurpriseMovie] = useState(null);
    const [isSurpriseLoading, setIsSurpriseLoading] = useState(false);
    const [surpriseError, setSurpriseError] = useState('');
    const [, setLastSurpriseParams] = useState({ contentType: 'film', origin: 'yabancı' }); // Son kullanılan parametreler (referans için)

    // Favori ve izleme durumu için state refresh
    const [refreshKey, setRefreshKey] = useState(0);

    // Trailer modal state'leri
    const [selectedTrailer, setSelectedTrailer] = useState(null);
    const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false);
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [currentMovieForTrailer, setCurrentMovieForTrailer] = useState('');

    // Toast bildirimi state'i
    const [toast, setToast] = useState(null);

    // Güvenli şekilde Google araması açar
    const openGoogleSearch = (title = '') => {
        const safeTitle = title.trim();
        if (!safeTitle) return;
        const q = encodeURIComponent(`${safeTitle} full izle`);
        // Yeni sekmede aç + güvenlik için noopener,noreferrer
        window.open(`https://www.google.com/search?q=${q}`, "_blank", "noopener,noreferrer");
    };


    // Confirmation modal state'leri
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'Onayla',
        cancelText: 'İptal',
        onConfirm: null,
        isLoading: false
    });

    // Ruh hali çeviri fonksiyonu
    const translateMoodToTurkish = (text) => {
        if (!text) return text;

        const moodTranslations = {
            'happy': 'mutlu',
            'sad': 'hüzünlü',
            'excited': 'heyecanlı',
            'relaxed': 'rahat',
            'romantic': 'romantik',
            'adventure': 'macera arayan'
        };

        let translatedText = text;
        Object.entries(moodTranslations).forEach(([english, turkish]) => {
            const regex = new RegExp(`\\b${english}\\b`, 'gi');
            translatedText = translatedText.replace(regex, turkish);
        });

        return translatedText;
    };

    const rawGenres = getGenres();
    // Film türlerini alfabetik sırala ve çevir
    const genreMap = React.useMemo(() => ({
        'Aksiyon': t('recommendation.genres.action'),
        'Komedi': t('recommendation.genres.comedy'),
        'Drama': t('recommendation.genres.drama'),
        'Korku': t('recommendation.genres.horror'),
        'Romantik': t('recommendation.genres.romance'),
        'Bilim Kurgu': t('recommendation.genres.scifi'),
        'Gerilim': t('recommendation.genres.thriller'),
        'Suç': t('recommendation.genres.crime'),
        'Macera': t('recommendation.genres.adventure'),
        'Fantastik': t('recommendation.genres.fantasy'),
        'Animasyon': t('recommendation.genres.animation'),
        'Aile': t('recommendation.genres.family')
    }), [t]);

    // Çevrilmiş türleri alfabetik sırala
    const genres = React.useMemo(() => {
        return rawGenres.map(genre => ({
            original: genre,
            translated: genreMap[genre] || genre
        })).sort((a, b) => a.translated.localeCompare(b.translated));
    }, [rawGenres, genreMap]);

    // Çevrilmiş türü orijinal türe çevir
    const getOriginalGenre = (translatedGenre) => {
        const found = genres.find(g => g.translated === translatedGenre);
        return found ? found.original : translatedGenre;
    };
    const moods = React.useMemo(() => [
        { id: 'happy', label: t('recommendation.moods.happy'), value: 'happy', icon: Smile },
        { id: 'sad', label: t('recommendation.moods.sad'), value: 'sad', icon: Frown },
        { id: 'excited', label: t('recommendation.moods.excited'), value: 'excited', icon: Zap },
        { id: 'relaxed', label: t('recommendation.moods.relaxed'), value: 'relaxed', icon: Coffee },
        { id: 'romantic', label: t('recommendation.moods.romantic'), value: 'romantic', icon: Heart },
        { id: 'adventure', label: t('recommendation.moods.adventure'), value: 'adventure', icon: Mountain }
    ], [t]);

    useEffect(() => {
        if (user?.preferences) {
            setFormData(prev => ({
                ...prev,
                genres: user.preferences.genres || []
            }));
        }
    }, [user]);

    // Global refresh sistemini kaydet (sonsuz döngü önleme ile)
    useEffect(() => {
        const unregister = registerGlobalRefresh(() => {
            // Sadece gerekli güncellemeleri yap, setRefreshKey kullanma
            forceUpdate();
        });

        const forceUpdate = () => {
            // Component'i yeniden render etmek için küçük bir trick
            setFormData(prev => ({ ...prev }));
        };

        return unregister; // Cleanup
    }, []);

    // Favori ve izlenen filmlerin durumlarını ekle (filtreleme YOK - tüm filmler gösterilir)
    const filterAndEnhanceRecommendations = (recommendations) => {
        if (!recommendations || !recommendations.recommendations) {
            return recommendations;
        }

        const favorites = getFavorites();
        const watchedMovies = getWatchedMovies();

        // TÜM filmlerin favori/izleme durumlarını ekle (filtreleme yapma)
        const enhancedMovies = recommendations.recommendations.map(movie => {
            const isMovieFavorite = favorites.some(fav =>
                fav.title === movie.title && fav.year === movie.year
            );
            const isMovieWatched = watchedMovies.some(watched =>
                watched.title === movie.title && watched.year === movie.year
            );

            return {
                ...movie,
                isFavorite: isMovieFavorite,
                isWatched: isMovieWatched,
                // ÖNEMLİ: posterUrl'i koru - hiçbir şekilde değiştirme
                posterUrl: movie.posterUrl
            };
        });

        return {
            ...recommendations,
            recommendations: enhancedMovies,
            filteredCount: 0 // Artık filtreleme yok
        };
    };

    const handleGenreToggle = (translatedGenre) => {
        const originalGenre = getOriginalGenre(translatedGenre);
        setFormData(prev => ({
            ...prev,
            genres: prev.genres.includes(originalGenre)
                ? prev.genres.filter(g => g !== originalGenre)
                : [...prev.genres, originalGenre]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // User ID'yi ekle
            const formDataWithUserId = {
                ...formData,
                userId: user?.id || null
            };

            const result = await getMovieRecommendations(formDataWithUserId);

            // Backend'den zaten poster URL'leri geldiği için, sadece contentType ekleyip kullan
            // TMDB'den tekrar poster çekmiyoruz - backend'den gelen poster URL'leri doğru ve güvenilir
            let finalRecommendations = result;
            if (result && result.recommendations) {
                // Sadece contentType ekle, poster URL'lerini değiştirme
                finalRecommendations = {
                    ...result,
                    recommendations: result.recommendations.map(movie => {
                        // Debug: Backend'den gelen poster URL'ini logla
                        console.log(`🎬 ${movie.title}: posterUrl=${movie.posterUrl}`);

                        return {
                            ...movie,
                            contentType: movie.contentType || formData.contentType || 'film',
                            // posterUrl zaten backend'den geldi, onu olduğu gibi kullan
                            // description içindeki poster URL'i göz ardı et
                            // ÖNEMLİ: description'dan poster URL'i parse etme ve kullanma
                            posterUrl: movie.posterUrl // Direkt backend'den gelen posterUrl'i kullan
                        };
                    })
                };
            }

            // Favori ve izlenen filmleri filtrele ve durumlarını ekle
            const filteredRecommendations = filterAndEnhanceRecommendations(finalRecommendations);

            setRecommendations(filteredRecommendations);
            finalRecommendations = filteredRecommendations;

            // Son kullanılan tercihleri kaydet (aynı tercihlerle yeniden öneri için)
            setLastUsedPreferences(formData);

            // Posterli önerileri localStorage'a kaydet (user.id ile kapsamlı)
            const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
            const savedRecommendations = JSON.parse(localStorage.getItem(recKey) || '[]');
            savedRecommendations.unshift(finalRecommendations); // Posterli versiyonu kaydet
            localStorage.setItem(recKey, JSON.stringify(savedRecommendations)); // Tüm önerileri sakla

            // ÖNERİLERİ SUPABASE'E KAYDET (Yeni tablo sistemi)
            if (user?.id) {
                try {
                    const { saveAllRecommendationsToSupabase, incrementSuggestionCount } = await import('../services/supabaseDataService');
                    await saveAllRecommendationsToSupabase(savedRecommendations, user.id);
                    // Yeni öneri eklendiği için sayacı artır
                    await incrementSuggestionCount();
                } catch (error) {
                    console.error('❌ Öneriler Supabase\'e kaydedilemedi:', error);
                    // Hata olsa bile localStorage'da var, devam et
                }
            }

            // Kullanıcı tercihlerini güncelle (genres için)
            if (updateUserPreferences) {
                try {
                    await updateUserPreferences({
                        genres: user?.preferences?.genres || [] // Ayarlardaki kalıcı türleri koru
                    });
                } catch (error) {
                    console.error('❌ Tercihler kaydedilemedi:', error);
                }
            }

            // Eğer kullanıcının ayarlarda kalıcı favori türü yoksa, form seçimlerini temizle
            if (!user?.preferences?.genres || user.preferences.genres.length === 0) {
                setTimeout(() => {
                    setFormData(prev => ({
                        ...prev,
                        genres: [], // Geçici seçimleri temizle
                        mood: ''   // Ruh halini de temizle
                    }));
                }, 2000); // 2 saniye sonra temizle (kullanıcı sonuçları görsün)
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            genres: [],
            mood: '',
            contentType: 'film',
            origin: 'yabancı'
        });
        setRecommendations(null);
        setError('');
        setLastUsedPreferences(null);
    };

    // Aynı tercihlerle yeniden öneri alma
    const handleRerollWithSamePreferences = async () => {
        // Son kullanılan tercihleri kullan, yoksa mevcut formData'yı kullan
        const preferencesToUse = lastUsedPreferences || formData;

        if (!preferencesToUse || preferencesToUse.genres.length === 0) {
            setError('Lütfen önce film türü seçin');
            return;
        }

        // Mevcut gösterilen 3 filmi al (tekrar önerilmemesi için)
        const currentTitles = recommendations?.recommendations
            ? recommendations.recommendations.map(m => m.title).filter(Boolean)
            : [];
        const currentTmdbIds = recommendations?.recommendations
            ? recommendations.recommendations.map(m => m.tmdbId || m.tmdb_id).filter(Boolean)
            : [];

        setIsLoading(true);
        setError('');

        try {
            // User ID'yi ekle ve mevcut filmleri exclude et
            const preferencesWithUserId = {
                ...preferencesToUse,
                userId: user?.id || null
            };

            const result = await getMovieRecommendations(preferencesWithUserId, currentTitles, currentTmdbIds);

            // TMDB posterlerini ekle
            let finalRecommendations = result;
            // Backend'den zaten poster URL'leri geldiği için, sadece contentType ekleyip kullan
            // TMDB'den tekrar poster çekmiyoruz - backend'den gelen poster URL'leri doğru ve güvenilir
            if (result && result.recommendations) {
                // Sadece contentType ekle, poster URL'lerini değiştirme
                finalRecommendations = {
                    ...result,
                    recommendations: result.recommendations.map(movie => ({
                        ...movie,
                        contentType: movie.contentType || preferencesToUse.contentType || 'film',
                        // posterUrl zaten backend'den geldi, onu olduğu gibi kullan
                        // description içindeki poster URL'i göz ardı et
                    }))
                };
            }

            // Favori ve izlenen filmleri filtrele ve durumlarını ekle
            const filteredRecommendations = filterAndEnhanceRecommendations(finalRecommendations);

            setRecommendations(filteredRecommendations);
            finalRecommendations = filteredRecommendations;

            // Posterli önerileri localStorage'a kaydet
            const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
            const savedRecommendations = JSON.parse(localStorage.getItem(recKey) || '[]');
            savedRecommendations.unshift(finalRecommendations);
            localStorage.setItem(recKey, JSON.stringify(savedRecommendations));

            // ÖNERİLERİ SUPABASE'E KAYDET (Yeni tablo sistemi - reroll)
            if (user?.id) {
                try {
                    const { saveAllRecommendationsToSupabase } = await import('../services/supabaseDataService');
                    await saveAllRecommendationsToSupabase(savedRecommendations, user.id);
                } catch (error) {
                    console.error('❌ Öneriler Supabase\'e kaydedilemedi:', error);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Emoji formatını parse etme fonksiyonu
    const parseMovieDescription = (description) => {
        if (!description) return {};

        const lines = description.split('\n');
        const parsed = {};

        lines.forEach(line => {
            if (line.includes('🎬 Film Adı:') || line.includes('🎬 Dizi Adı:')) {
                parsed.title = line.replace(/🎬 (Film|Dizi) Adı:/, '').trim();
            } else if (line.includes('📆 Yıl:')) {
                parsed.year = line.replace('📆 Yıl:', '').trim();
            } else if (line.includes('🌟 IMDb Puanı:')) {
                parsed.rating = line.replace('🌟 IMDb Puanı:', '').trim();
            } else if (line.includes('📝 Kısa Açıklama:')) {
                parsed.explanation = line.replace('📝 Kısa Açıklama:', '').trim();
            } else if (line.includes('📝 Bugün İçin Mükemmel:')) {
                parsed.explanation = line.replace('📝 Bugün İçin Mükemmel:', '').trim();
            }
            // posterUrl artık parse edilmiyor - backend'den gelen movie.posterUrl kullanılıyor
            // Bu, yanlış poster URL'lerinin description'dan gelmesini önler
        });

        return parsed;
    };

    // Film kartına tıklama - cast bilgilerini yükle
    const handleMovieClick = async (movie) => {
        setSelectedMovie(movie);

        // Eğer oyuncu listesi eksikse veya yoksa, TMDB'den çek
        if (!movie.actors || movie.actors.length < 3) {
            try {
                const contentType = movie.contentType || formData.contentType || 'film';
                const movieDetails = await getMovieDetailsWithCast(movie.title, movie.year, contentType);

                if (movieDetails && movieDetails.cast && movieDetails.cast.length > 0) {
                    // Cast bilgilerini film objesine ekle
                    setSelectedMovie({
                        ...movie,
                        actors: movieDetails.cast,
                        posterUrl: movieDetails.posterUrl || movie.posterUrl, // Yüksek çözünürlük poster
                        tmdbId: movieDetails.id
                    });
                }
            } catch (error) {
                console.error('❌ Cast bilgisi yükleme hatası:', error);
                // Hata olsa bile mevcut film bilgilerini göster
            }
        }
    };

    // Modal kapatma
    const closeModal = () => {
        setSelectedMovie(null);
    };

    // Sürpriz film/dizi önerisi alma - RASTGELE (film/dizi farketmez, tüm dünyadan)
    const handleSurpriseMovie = async () => {
        // Çoklu çağrıları önle - eğer zaten loading ise, yeni çağrıyı engelle
        if (isSurpriseLoading) {
            console.warn('⚠️ Sürpriz öneri zaten yükleniyor, yeni çağrı engellendi');
            return;
        }

        // RASTGELE seçim: Film mi dizi mi - tamamen rastgele!
        // Origin göndermiyoruz - backend tüm dünyadan içerik önerecek
        const randomContentType = Math.random() < 0.5 ? 'film' : 'dizi';

        const finalContentType = randomContentType;
        // Origin parametresi göndermiyoruz - backend'de "any" olarak işlenecek

        // Parametreleri kaydet (sonraki sürprizler için)
        setLastSurpriseParams({ contentType: finalContentType, origin: 'any' });

        // Önce state'leri temizle
        setSurpriseMovie(null);
        setSurpriseError('');

        // Kısa bir gecikme ekle (modal'ın kapanması için)
        await new Promise(resolve => setTimeout(resolve, 100));

        // Sonra loading başlat
        setIsSurpriseLoading(true);

        try {
            // Önceki sürpriz önerileri al (tekrar önlemek için)
            const recKey = user?.id ? `film_recommendations:${user.id}` : 'film_recommendations';
            const savedRecommendations = JSON.parse(localStorage.getItem(recKey) || '[]');

            // Sadece sürpriz önerileri filtrele (explanation'da "Sürpriz" geçenler)
            const previousSurpriseTitles = savedRecommendations
                .filter(rec => rec.explanation && rec.explanation.includes('Sürpriz'))
                .flatMap(rec => rec.recommendations || [])
                .map(m => m.title)
                .filter(Boolean)
                .slice(-15); // Son 15 sürpriz öneriyi al

            // Origin parametresi göndermiyoruz - backend tüm dünyadan içerik önerecek
            // Önceki sürpriz önerileri de gönder (tekrar önlemek için)
            const result = await getSurpriseMovie(finalContentType, null, previousSurpriseTitles);

            // TMDB poster ekle
            try {
                const posterUrl = await getMoviePoster(result.title, result.year, finalContentType);
                result.posterUrl = posterUrl;
            } catch (posterError) {
                // Poster hatası durumunda sessizce devam et
            }

            setSurpriseMovie(result);

            // Sürpriz filmi/diziyi geçmişe kaydet (user.id ile kapsamlı)
            // recKey ve savedRecommendations zaten yukarıda tanımlı, tekrar tanımlama
            const contentTypeLabel = finalContentType === 'dizi' ? 'dizi' : 'film';
            const surpriseRecommendation = {
                recommendations: [result],
                explanation: `🎲 Sürpriz ${contentTypeLabel} önerisi: ${result.surprise_explanation || 'Bugün için mükemmel bir tercih!'}`,
                generatedAt: new Date().toISOString(),
                contentType: finalContentType,
                origin: 'any'
            };
            savedRecommendations.unshift(surpriseRecommendation);
            localStorage.setItem(recKey, JSON.stringify(savedRecommendations)); // Tüm önerileri sakla

            // SÜRPRIZ ÖNERİYİ SUPABASE'E KAYDET - TEK ÖNERİ OLARAK (sayacı doğru artırmak için)
            if (user?.id) {
                try {
                    const { saveRecommendationToSupabase } = await import('../services/supabaseDataService');
                    // Tek öneri kaydet - bu fonksiyon sayacı otomatik artırır
                    await saveRecommendationToSupabase(surpriseRecommendation, user.id);
                } catch (error) {
                    console.error('❌ Sürpriz öneri Supabase\'e kaydedilemedi:', error);
                    // Hata olsa bile localStorage'da var, devam et
                }
            }

        } catch (err) {
            console.error('❌ Sürpriz öneri hatası:', err);
            setSurpriseError(err.message || 'Sürpriz öneri alınırken bir hata oluştu');
        } finally {
            // Loading state'i her durumda kapat
            setIsSurpriseLoading(false);
        }
    };

    // Sürpriz modal kapatma
    const closeSurpriseModal = () => {
        setSurpriseMovie(null);
        setSurpriseError('');
    };

    // Confirmation modal açma
    const openConfirmationModal = (config) => {
        setConfirmationModal({
            isOpen: true,
            ...config
        });
    };

    // Confirmation modal kapatma
    const closeConfirmationModal = () => {
        setConfirmationModal(prev => ({
            ...prev,
            isOpen: false,
            isLoading: false
        }));
    };

    // Confirmation modal onaylama
    const handleConfirmationConfirm = async () => {
        if (confirmationModal.onConfirm) {
            setConfirmationModal(prev => ({ ...prev, isLoading: true }));
            try {
                await confirmationModal.onConfirm();
                closeConfirmationModal();
            } catch (error) {
                console.error('Confirmation action failed:', error);
                setConfirmationModal(prev => ({ ...prev, isLoading: false }));
            }
        }
    };

    // Favori durumu toggle
    const handleFavoriteToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyFavorite = isFavorite(movieId, movie.title);

        if (isCurrentlyFavorite) {
            // Favorilerden çıkarma onayı
            openConfirmationModal({
                title: t('favorites.removeFromFavoritesConfirm'),
                message: t('favorites.removeFromFavoritesMessage', { title: movie.title }),
                type: 'danger',
                confirmText: t('favorites.remove'),
                cancelText: t('common.cancel'),
                onConfirm: () => {
                    const success = removeFromFavorites(movieId, movie.title);
                    setRefreshKey(prev => prev + 1);

                    // Toast bildirimi
                    if (success) {
                        setToast({
                            message: t('favorites.removedFromFavorites', { title: movie.title }),
                            type: 'success'
                        });
                    } else {
                        setToast({
                            message: t('favorites.removeError', { title: movie.title }),
                            type: 'error'
                        });
                    }

                    // Sürpriz film state'ini de güncelle
                    if (surpriseMovie && surpriseMovie.id === movieId) {
                        setSurpriseMovie({ ...surpriseMovie });
                    }
                    // Modal state'ini de güncelle
                    if (selectedMovie && selectedMovie.id === movieId) {
                        setSelectedMovie({ ...selectedMovie });
                    }
                }
            });
        } else {
            // Favorilere ekleme (onaysız)
            const success = addToFavorites(movie);
            setRefreshKey(prev => prev + 1);

            // Toast bildirimi
            if (success) {
                setToast({
                    message: t('favorites.addedToFavorites', { title: movie.title }),
                    type: 'success'
                });
            } else {
                setToast({
                    message: t('favorites.addError', { title: movie.title }),
                    type: 'error'
                });
            }

            // Sürpriz film state'ini de güncelle
            if (surpriseMovie && surpriseMovie.id === movieId) {
                setSurpriseMovie({ ...surpriseMovie });
            }
            // Modal state'ini de güncelle
            if (selectedMovie && selectedMovie.id === movieId) {
                setSelectedMovie({ ...selectedMovie });
            }
        }
    };

    // İzleme durumu toggle
    const handleWatchedToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyWatched = isWatched(movieId);

        if (isCurrentlyWatched) {
            // İzlenmedi olarak işaretleme onayı
            openConfirmationModal({
                title: t('favorites.markAsUnwatchedConfirm'),
                message: t('favorites.markAsUnwatchedMessage', { title: movie.title }),
                type: 'warning',
                confirmText: t('favorites.confirmUnwatched'),
                cancelText: t('common.cancel'),
                onConfirm: () => {
                    const success = markAsUnwatched(movieId);
                    setRefreshKey(prev => prev + 1);

                    // Toast bildirimi
                    if (success) {
                        setToast({
                            message: t('favorites.markedAsUnwatched', { title: movie.title }),
                            type: 'success'
                        });
                    } else {
                        setToast({
                            message: t('favorites.markUnwatchedError', { title: movie.title }),
                            type: 'error'
                        });
                    }

                    // Sürpriz film state'ini de güncelle
                    if (surpriseMovie && surpriseMovie.id === movieId) {
                        setSurpriseMovie({ ...surpriseMovie });
                    }
                    // Modal state'ini de güncelle
                    if (selectedMovie && selectedMovie.id === movieId) {
                        setSelectedMovie({ ...selectedMovie });
                    }
                }
            });
        } else {
            // İzlendi olarak işaretleme (onaysız)
            const success = markAsWatched(movie);
            setRefreshKey(prev => prev + 1);

            // Toast bildirimi
            if (success) {
                setToast({
                    message: t('favorites.markedAsWatched', { title: movie.title }),
                    type: 'success'
                });
            } else {
                setToast({
                    message: t('favorites.markWatchedError', { title: movie.title }),
                    type: 'error'
                });
            }

            // Sürpriz film state'ini de güncelle
            if (surpriseMovie && surpriseMovie.id === movieId) {
                setSurpriseMovie({ ...surpriseMovie });
            }
            // Modal state'ini de güncelle
            if (selectedMovie && selectedMovie.id === movieId) {
                setSelectedMovie({ ...selectedMovie });
            }
        }
    };

    // Fragman açma fonksiyonu
    const handleWatchTrailer = async (movie) => {
        try {
            // ÖNEMLİ: Film detay modal'ını kapat (çakışmayı önle)
            setSelectedMovie(null);

            setTrailerLoading(true);
            setCurrentMovieForTrailer(movie.title);

            const trailer = await getMovieTrailer(movie.title, movie.year);

            setSelectedTrailer(trailer);
            setIsTrailerModalOpen(true);

        } catch (error) {
            console.error('❌ Fragman yükleme hatası:', error);
            // Hata durumunda da modal aç (fragman bulunamadı mesajı için)
            setSelectedTrailer(null);
            setIsTrailerModalOpen(true);
        } finally {
            setTrailerLoading(false);
        }
    };

    // Trailer modal kapatma
    const closeTrailerModal = () => {
        setIsTrailerModalOpen(false);
        setSelectedTrailer(null);
        setCurrentMovieForTrailer('');
    };

    // Detay Modal Komponenti
    const MovieDetailModal = ({ movie, onClose }) => {
        // Hook'ları early return'den ÖNCE çağırmalıyız (React Hook kuralları)
        const [castLoading, setCastLoading] = React.useState(false);
        const [enhancedCast, setEnhancedCast] = React.useState(movie?.actors || []);

        // Eğer cast eksikse, TMDB'den yükle
        React.useEffect(() => {
            // Movie yoksa çalışma
            if (!movie) return;

            const loadCast = async () => {
                // Eğer zaten 5'ten fazla oyuncu varsa veya yükleniyorsa, yükleme
                if (enhancedCast.length >= 5 || castLoading) return;

                // Eğer oyuncu listesi eksikse (3'ten az veya yoksa)
                if (!movie.actors || movie.actors.length < 3) {
                    setCastLoading(true);
                    try {
                        const contentType = movie.contentType || formData.contentType || 'film';
                        const movieDetails = await getMovieDetailsWithCast(movie.title, movie.year, contentType);

                        if (movieDetails && movieDetails.cast && movieDetails.cast.length > 0) {
                            setEnhancedCast(movieDetails.cast);
                        }
                    } catch (error) {
                        console.error('❌ Cast bilgisi yükleme hatası:', error);
                    } finally {
                        setCastLoading(false);
                    }
                }
            };

            loadCast();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [movie?.id, movie?.title, movie?.year, movie?.contentType]);

        // Early return hook'lardan SONRA yapılmalı
        if (!movie) return null;

        const parsedInfo = parseMovieDescription(movie.description);

        // Modal dışına tıklandığında kapatma fonksiyonu
        const handleOverlayClick = (e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        };

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={handleOverlayClick}
            >
                <div
                    className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all"
                    >
                        <X className="h-6 w-6" />
                    </button>

                    {/* Main Content - Horizontal Layout */}
                    <div className="flex flex-col lg:flex-row">
                        {/* Left Side - Poster */}
                        <div className="lg:w-1/3 p-6">
                            <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 rounded-xl overflow-hidden shadow-xl">
                                {movie.posterUrl && movie.posterUrl !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' ? (
                                    <img
                                        src={movie.posterUrl}
                                        alt={movie.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700">
                                        <div className="text-center p-4">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                                                <Film className="h-20 w-20 mx-auto mb-4 opacity-90" />
                                                <p className="text-xl font-bold mb-2">{movie.title}</p>
                                                <p className="text-sm opacity-80">{parsedInfo.year || movie.year}</p>
                                                <div className="mt-3 flex items-center justify-center space-x-1">
                                                    <span className="text-yellow-300 text-lg">⭐</span>
                                                    <span className="text-base font-medium">{parsedInfo.rating || movie.rating}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rating Badge on Poster */}
                                <div className="absolute top-3 right-3 bg-black bg-opacity-80 text-white px-3 py-1 rounded-full">
                                    <span className="font-bold text-sm">🌟 {parsedInfo.rating || movie.rating}</span>
                                </div>

                                {/* Durum Göstergeleri */}
                                <div className="absolute top-3 left-3 flex flex-col gap-2">
                                    {isFavorite(movie.id, movie.title) && (
                                        <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Heart className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                    {isWatched(movie.id) && (
                                        <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Eye className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Movie Info */}
                        <div className="lg:w-2/3 p-6 lg:pl-0">
                            {/* Movie Title */}
                            <div className="mb-6">
                                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                                    🎬 {parsedInfo.title || movie.title}
                                </h1>
                            </div>

                            {/* Movie Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                                        {parsedInfo.rating || movie.rating}
                                    </div>
                                    <div className="text-sm text-yellow-600 dark:text-yellow-500 font-medium">IMDb Puanı</div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                        {parsedInfo.year || movie.year}
                                    </div>
                                    <div className="text-sm text-blue-600 dark:text-blue-500 font-medium">{t('movie.productionYear')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                                        {movie.duration || '120'}
                                    </div>
                                    <div className="text-sm text-green-600 dark:text-green-500 font-medium">{t('movie.minutes')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-xl text-center">
                                    <div className="text-lg font-bold text-purple-700 dark:text-purple-400">
                                        {movie.genre}
                                    </div>
                                    <div className="text-sm text-purple-600 dark:text-purple-500 font-medium">{t('movie.genre')}</div>
                                </div>
                            </div>

                            {/* Cast */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                                    🎭 <span className="ml-2">{t('movie.actors')}</span>
                                    {castLoading && (
                                        <Loader className="h-4 w-4 ml-2 animate-spin text-purple-600" />
                                    )}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {enhancedCast.length > 0 ? (
                                        enhancedCast.map((actor, index) => (
                                            <span key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                                {actor}
                                            </span>
                                        ))
                                    ) : (
                                        movie.actors && movie.actors.length > 0 ? (
                                            movie.actors.map((actor, index) => (
                                                <span key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                                    {actor}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-sm italic">
                                                {t('movie.actors')} {t('common.loading')}...
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Actions, Plot and Recommendation */}
                    <div className="px-6 pb-6 space-y-6">
                        {/* Favori ve İzleme Butonları */}
                        <div className="flex gap-3 justify-center flex-wrap">
                            <button
                                onClick={() => handleFavoriteToggle(movie)}
                                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                    }`}
                            >
                                <Heart className={`h-5 w-5 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                                <span className="hidden sm:inline">{isFavorite(movie.id, movie.title) ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchedToggle(movie)}
                                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-200 dark:border-green-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    }`}
                            >
                                {isWatched(movie.id) ? (
                                    <Eye className="h-5 w-5 fill-current" />
                                ) : (
                                    <EyeOff className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">{isWatched(movie.id) ? t('favorites.watchedShort') : t('favorites.markAsWatched')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchTrailer(movie)}
                                disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                            >
                                {trailerLoading && currentMovieForTrailer === movie.title ? (
                                    <Loader className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Play className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">
                                    {trailerLoading && currentMovieForTrailer === movie.title
                                        ? t('favorites.searching')
                                        : t('favorites.watchTrailer')
                                    }
                                </span>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openGoogleSearch(movie.title || movie.name);
                                }}
                                className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                            >
                                <Search className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('favorites.searchOnGoogle')}</span>
                            </button>

                        </div>

                        {/* Plot */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                📖 <span className="ml-2">{t('favorites.moviePlot')}</span>
                            </h3>
                            <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                    {movie.plot || t('favorites.noPlotAvailable')}
                                </p>
                            </div>
                        </div>

                        {/* Why Recommended */}
                        {parsedInfo.explanation && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    💡 <span className="ml-2">{t('recommendation.whyRecommended')}</span>
                                </h3>
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-l-4 border-purple-500">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                        {parsedInfo.explanation}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Sürpriz Film Modal Komponenti
    const SurpriseMovieModal = ({ movie, onClose, error }) => {
        if (!movie && !error) {
            return null;
        }

        const handleOverlayClick = (e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        };

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
                onClick={handleOverlayClick}
                style={{ zIndex: 10000, position: 'fixed' }}
            >
                <div
                    className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all"
                    >
                        <X className="h-6 w-6" />
                    </button>

                    {error ? (
                        <div className="p-8 text-center">
                            <div className="text-red-500 text-6xl mb-4">😞</div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('recommendation.surpriseNotReady')}</h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                            <button
                                onClick={onClose}
                                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                {t('recommendation.tryAgain')}
                            </button>
                        </div>
                    ) : (
                        <div className="p-6">
                            {/* Header */}
                            <div className="text-center mb-6">
                                <div className="flex justify-center items-center space-x-2 mb-2">
                                    <Shuffle className="h-8 w-8 text-purple-600" />
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">🎲 Sürpriz Film!</h2>
                                </div>
                                <p className="text-purple-600 dark:text-purple-400 text-sm">Bugün için özel seçildi</p>
                            </div>

                            {/* Movie Card */}
                            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 mb-6">
                                <div className="flex flex-col md:flex-row gap-6">
                                    {/* Poster */}
                                    <div className="md:w-48 mx-auto md:mx-0">
                                        <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 rounded-xl overflow-hidden shadow-xl">
                                            {movie.posterUrl && movie.posterUrl !== 'https://image.tmdb.org/t/p/w500/default-poster.jpg' ? (
                                                <img
                                                    src={movie.posterUrl}
                                                    alt={movie.title}
                                                    className="w-full h-full object-cover rounded-xl"
                                                />
                                            ) : (
                                                <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700">
                                                    <div className="text-center p-4">
                                                        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                                                            <Film className="h-12 w-12 mx-auto mb-3 opacity-90" />
                                                            <p className="text-base font-bold mb-1">{movie.title}</p>
                                                            <p className="text-xs opacity-80">{movie.year}</p>
                                                            <div className="mt-2 flex items-center justify-center space-x-1">
                                                                <span className="text-yellow-300">⭐</span>
                                                                <span className="text-xs font-medium">{movie.rating}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Rating Badge */}
                                            <div className="absolute top-3 right-3 bg-black bg-opacity-80 text-white px-2 py-1 rounded-full">
                                                <span className="font-bold text-sm">🌟 {movie.rating}</span>
                                            </div>

                                            {/* Durum Göstergeleri */}
                                            <div className="absolute top-3 left-3 flex flex-col gap-2">
                                                {isFavorite(movie.id, movie.title) && (
                                                    <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                        <Heart className="h-4 w-4 fill-current" />
                                                    </div>
                                                )}
                                                {isWatched(movie.id) && (
                                                    <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                        <Eye className="h-4 w-4 fill-current" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Movie Info */}
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                                            🎬 {movie.title}
                                        </h3>

                                        {/* Surprise Explanation */}
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
                                            <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">🎯 Neden Bu Film?</h4>
                                            <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                                                {movie.surprise_explanation || movie.plot}
                                            </p>
                                        </div>

                                        {/* Actors */}
                                        <div className="mb-4">
                                            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                                                🎭 <span className="ml-1">Oyuncular</span>
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {movie.actors?.map((actor, index) => (
                                                    <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-xs">
                                                        {actor}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Favori ve İzleme Butonları */}
                            <div className="flex gap-3 justify-center mb-6 flex-wrap">
                                <button
                                    onClick={() => handleFavoriteToggle(movie)}
                                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-700'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                        }`}
                                >
                                    <Heart className={`h-5 w-5 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                                    <span className="hidden sm:inline">{isFavorite(movie.id, movie.title) ? 'Favori' : 'Favori'}</span>
                                </button>

                                <button
                                    onClick={() => handleWatchedToggle(movie)}
                                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-200 dark:border-green-700'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                        }`}
                                >
                                    {isWatched(movie.id) ? (
                                        <Eye className="h-5 w-5 fill-current" />
                                    ) : (
                                        <EyeOff className="h-5 w-5" />
                                    )}
                                    <span className="hidden sm:inline">{isWatched(movie.id) ? t('favorites.watchedShort') : t('favorites.markAsWatched')}</span>
                                </button>

                                <button
                                    onClick={() => handleWatchTrailer(movie)}
                                    disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                    className="flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                                >
                                    {trailerLoading && currentMovieForTrailer === movie.title ? (
                                        <Loader className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <Play className="h-5 w-5" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {trailerLoading && currentMovieForTrailer === movie.title
                                            ? t('favorites.searching')
                                            : t('favorites.watchTrailer')
                                        }
                                    </span>
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        openGoogleSearch(movie.title || movie.name);
                                    }}
                                    className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                                >
                                    <Search className="h-4 w-4" />
                                    <span className="hidden sm:inline">{t('favorites.searchOnGoogle')}</span>
                                </button>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={handleSurpriseMovie}
                                    disabled={isSurpriseLoading}
                                    className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                                >
                                    <Shuffle className="h-5 w-5" />
                                    <span>Başka Sürpriz</span>
                                </button>
                                <button
                                    onClick={onClose}
                                    className="flex items-center space-x-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                    <span>{t('recommendation.close')}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (recommendations) {
        return (
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                <div className="text-center mb-6 sm:mb-8">
                    <div className="flex justify-center items-center space-x-2 mb-4">
                        <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('recommendation.yourRecommendations')}</h1>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-4">{translateMoodToTurkish(recommendations.explanation)}</p>
                    <p className="text-sm text-purple-600 dark:text-purple-400 mt-2 px-4">{t('favorites.clickForDetails')}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                    {recommendations.recommendations.map((movie) => {
                        const parsedInfo = parseMovieDescription(movie.description);

                        // Debug: Render sırasında poster URL'ini logla
                        console.log(`🖼️ Render: ${movie.title} - posterUrl: ${movie.posterUrl}`);

                        // ÖNEMLİ: Sadece movie.posterUrl kullan, parsedInfo.posterUrl'i hiç kullanma
                        const finalPosterUrl = movie.posterUrl;

                        return (
                            <div
                                key={movie.id}
                                onClick={(e) => {
                                    handleMovieClick(movie);
                                }}
                                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group max-w-sm mx-auto"
                            >
                                {/* Poster */}
                                <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 overflow-hidden">
                                    {finalPosterUrl && finalPosterUrl !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' ? (
                                        <img
                                            src={finalPosterUrl}
                                            alt={movie.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            onError={(e) => {
                                                console.error(`❌ Poster yükleme hatası: ${movie.title} - URL: ${finalPosterUrl}`);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700 group-hover:scale-105 transition-transform duration-300">
                                            <div className="text-center p-4">
                                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                                    <Film className="h-12 w-12 mx-auto mb-3 opacity-90" />
                                                    <p className="text-sm font-bold mb-1">{movie.title}</p>
                                                    <p className="text-xs opacity-80">{parsedInfo.year || movie.year}</p>
                                                    <div className="mt-2 flex items-center justify-center space-x-1">
                                                        <span className="text-yellow-300">⭐</span>
                                                        <span className="text-xs font-medium">{parsedInfo.rating || movie.rating}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Rating Badge */}
                                    <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full">
                                        <span className="font-bold text-xs sm:text-sm">🌟 {parsedInfo.rating || movie.rating}</span>
                                    </div>

                                    {/* Durum Göstergeleri */}
                                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                                        {(movie.isFavorite || isFavorite(movie.id, movie.title)) && (
                                            <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                <Heart className="h-4 w-4 fill-current" />
                                            </div>
                                        )}
                                        {(movie.isWatched || isWatched(movie.id)) && (
                                            <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                                <Eye className="h-4 w-4 fill-current" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Click Indicator */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white bg-opacity-90 rounded-full p-3">
                                            <Search className="h-6 w-6 text-purple-600" />
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-4 sm:p-6">
                                    <div className="mb-4">
                                        {/* Film Başlığı */}
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                            🎬 {parsedInfo.title || movie.title}
                                        </h3>

                                        {/* Yıl ve Tür */}
                                        <div className="flex items-center justify-between text-sm mb-4">
                                            <span className="text-gray-600 dark:text-gray-400">
                                                📆 {parsedInfo.year || movie.year}
                                            </span>
                                            <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-800 dark:text-purple-300 rounded-full text-xs font-medium">
                                                {movie.genre}
                                            </span>
                                        </div>

                                        {/* Süre */}
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                            ⏱️ {movie.duration || 120} {t('movie.minutes')}
                                        </div>
                                    </div>

                                    {/* Kısa Açıklama */}
                                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4">
                                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed line-clamp-3">
                                            <span className="font-medium text-purple-600 dark:text-purple-400">📝 </span>
                                            {parsedInfo.explanation || movie.description}
                                        </p>
                                    </div>

                                    {/* Favori ve İzleme Butonları */}
                                    <div className="mt-4 flex justify-center gap-2 flex-wrap">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                handleFavoriteToggle(movie);
                                            }}
                                            className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${(movie.isFavorite || isFavorite(movie.id, movie.title))
                                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                }`}
                                        >
                                            <Heart className={`h-4 w-4 ${(movie.isFavorite || isFavorite(movie.id, movie.title)) ? 'fill-current' : ''}`} />
                                            <span className="hidden sm:inline">{(movie.isFavorite || isFavorite(movie.id, movie.title)) ? 'Favori' : 'Favori'}</span>
                                        </button>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                handleWatchedToggle(movie);
                                            }}
                                            className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${(movie.isWatched || isWatched(movie.id))
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                }`}
                                        >
                                            {(movie.isWatched || isWatched(movie.id)) ? (
                                                <Eye className="h-4 w-4 fill-current" />
                                            ) : (
                                                <EyeOff className="h-4 w-4" />
                                            )}
                                            <span className="hidden sm:inline">{(movie.isWatched || isWatched(movie.id)) ? t('favorites.watchedShort') : t('favorites.markAsWatched')}</span>
                                        </button>

                                        {/* Fragman Butonu */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Kartın tıklama eventi ile karışmasın
                                                e.preventDefault();
                                                handleWatchTrailer(movie);
                                            }}
                                            disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                            className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                                        >
                                            {trailerLoading && currentMovieForTrailer === movie.title ? (
                                                <Loader className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Play className="h-4 w-4" />
                                            )}
                                            <span className="hidden sm:inline">
                                                {trailerLoading && currentMovieForTrailer === movie.title
                                                    ? t('favorites.searching')
                                                    : t('favorites.trailer')
                                                }
                                            </span>
                                        </button>
                                    </div>

                                    {/* Tıklama İpucu */}
                                    <div className="mt-3 text-center">
                                        <span className="text-xs text-purple-600 dark:text-purple-400 opacity-70">
                                            {t('favorites.clickForDetails')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Modal */}
                {selectedMovie && (
                    <MovieDetailModal
                        key={`${selectedMovie.id}-${refreshKey}`}
                        movie={selectedMovie}
                        onClose={closeModal}
                    />
                )}

                {/* Sürpriz Film Modal */}
                {(surpriseMovie || surpriseError) && (
                    <SurpriseMovieModal
                        key={`surprise-${surpriseMovie?.id}-${refreshKey}`}
                        movie={surpriseMovie}
                        error={surpriseError}
                        onClose={closeSurpriseModal}
                    />
                )}

                <div className="text-center flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
                    <button
                        onClick={handleRerollWithSamePreferences}
                        disabled={isLoading}
                        className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 sm:px-8 py-4 rounded-xl font-medium hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2 w-full sm:w-auto"
                    >
                        {isLoading ? (
                            <>
                                <Loader className="h-5 w-5 animate-spin" />
                                <span className="text-sm sm:text-base">Yeniden Öneriliyor...</span>
                            </>
                        ) : (
                            <>
                                <Shuffle className="h-5 w-5" />
                                <span className="text-sm sm:text-base">Aynı Tercihlerle Yeniden Öner</span>
                            </>
                        )}
                    </button>
                    <button
                        onClick={resetForm}
                        disabled={isLoading}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 sm:px-10 py-4 rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg w-full sm:w-auto"
                    >
                        <span className="text-sm sm:text-base">Yeni Öneri Al</span>
                    </button>
                </div>

                {/* Trailer Modal - ÖNEMLİ: Öneriler sayfasında da olmalı */}
                <TrailerModal
                    trailer={selectedTrailer}
                    isOpen={isTrailerModalOpen}
                    onClose={closeTrailerModal}
                    movieTitle={currentMovieForTrailer}
                />
            </div>
        );
    }

    // AI Loading Animasyonu Komponenti
    const AILoadingAnimation = () => (
        <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-800 flex items-center justify-center overflow-hidden">
            {/* Film şeridi arka plan animasyonu - Sürekli akan */}
            <div className="absolute inset-0 overflow-hidden opacity-10">
                {/* Yatay film şeritleri - Sürekli döngü */}
                {[...Array(3)].map((_, i) => (
                    <div
                        key={`horizontal-${i}`}
                        className="absolute h-20 sm:h-24 md:h-28 animate-film-strip-continuous"
                        style={{
                            top: `${i * 33.33}%`,
                            width: '200%'
                        }}
                    >
                        <div className="flex items-center h-full space-x-3 sm:space-x-4 md:space-x-5">
                            {[...Array(50)].map((_, j) => (
                                <div
                                    key={j}
                                    className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-white/25 rounded flex-shrink-0 border border-white/10"
                                ></div>
                            ))}
                        </div>
                    </div>
                ))}

                {/* Parlayan yıldızlar */}
                {[...Array(15)].map((_, i) => (
                    <div
                        key={`star-${i}`}
                        className="absolute animate-twinkle"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`
                        }}
                    >
                        <Star className="h-3 w-3 text-yellow-300 fill-current" />
                    </div>
                ))}
            </div>

            {/* Ana içerik */}
            <div className="relative z-10 text-center px-4">
                {/* Ana film görseli animasyonu */}
                <div className="mb-8 relative">
                    <div className="relative w-40 h-40 mx-auto flex items-center justify-center">
                        {/* Dönen halkalar */}
                        <div className="absolute inset-0 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                        <div className="absolute inset-3 border-4 border-indigo-400 border-b-transparent rounded-full animate-spin-slow"></div>
                        <div className="absolute inset-6 border-2 border-pink-400 border-l-transparent rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>

                        {/* Merkez - Film Kamerası İkonu */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 flex items-center justify-center animate-pulse">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" className="w-full h-full text-indigo-300 drop-shadow-2xl" fill="none">
                                    <defs>
                                        <linearGradient id="camGrad" x1="0" y1="0" x2="128" y2="128" gradientUnits="userSpaceOnUse">
                                            <stop offset="0" stopColor="currentColor" stopOpacity="0.95" />
                                            <stop offset="1" stopColor="currentColor" stopOpacity="0.75" />
                                        </linearGradient>

                                        <mask id="reelMaskLarge">
                                            <rect width="100%" height="100%" fill="black" />
                                            <circle cx="40" cy="36" r="22" fill="white" />
                                            <circle cx="40" cy="36" r="6" fill="black" />
                                            <circle cx="49" cy="36" r="4.5" fill="black" />
                                            <circle cx="31" cy="36" r="4.5" fill="black" />
                                            <circle cx="40" cy="26.5" r="4.5" fill="black" />
                                            <circle cx="40" cy="45.5" r="4.5" fill="black" />
                                        </mask>

                                        <mask id="reelMaskSmall">
                                            <rect width="100%" height="100%" fill="black" />
                                            <circle cx="78" cy="30" r="18" fill="white" />
                                            <circle cx="78" cy="30" r="5" fill="black" />
                                            <circle cx="86" cy="30" r="3.5" fill="black" />
                                            <circle cx="70" cy="30" r="3.5" fill="black" />
                                            <circle cx="78" cy="22" r="3.5" fill="black" />
                                            <circle cx="78" cy="38" r="3.5" fill="black" />
                                        </mask>
                                    </defs>

                                    {/* Body */}
                                    <rect x="16" y="54" width="78" height="48" rx="10" fill="url(#camGrad)" />
                                    {/* Lens */}
                                    <polygon points="94,58 120,48 120,108 94,98" fill="currentColor" opacity="0.9" />

                                    {/* Dekor: film penceresi */}
                                    <rect x="24" y="66" width="62" height="10" rx="3" fill="#ffffff" opacity="0.2" />
                                    <g fill="#ffffff" opacity="0.55">
                                        <rect x="28" y="68" width="6" height="6" rx="1.5" />
                                        <rect x="38" y="68" width="6" height="6" rx="1.5" />
                                        <rect x="48" y="68" width="6" height="6" rx="1.5" />
                                        <rect x="58" y="68" width="6" height="6" rx="1.5" />
                                        <rect x="68" y="68" width="6" height="6" rx="1.5" />
                                    </g>

                                    {/* Reels (delikli makaralar) */}
                                    <g fill="currentColor">
                                        <rect width="128" height="128" mask="url(#reelMaskLarge)" />
                                        <rect width="128" height="128" mask="url(#reelMaskSmall)" />
                                    </g>

                                    {/* Çizgiler */}
                                    <rect x="16" y="54" width="78" height="48" rx="10" stroke="currentColor" strokeWidth="2" opacity="0.45" />
                                    <path d="M94 58 L120 48 V108 L94 98 Z" stroke="#000" strokeOpacity="0.15" />
                                </svg>
                            </div>

                            {/* Parlama efekti */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-14 h-14 bg-purple-400/30 rounded-full blur-lg animate-ping"></div>
                            </div>
                        </div>


                    </div>

                    {/* Parlama efekti */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-56 h-56 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-indigo-500/20 rounded-full blur-3xl animate-pulse"></div>
                    </div>
                </div>

                {/* Başlık ve mesajlar */}
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 animate-fade-in">
                    {t('recommendation.searchingMovies')}
                </h2>



                {/* İlerleme çubuğu */}
                <div className="w-full max-w-md mx-auto mb-8">
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                        <div className="h-full bg-gradient-to-r from-yellow-400 via-purple-500 to-indigo-500 rounded-full animate-loading-bar"></div>
                    </div>
                </div>

                {/* Alt bilgi */}
                <div className="flex items-center justify-center space-x-2 text-purple-300 animate-fade-in-delay-3">
                    <Clock className="h-5 w-5 animate-pulse" />
                    <span className="text-sm">{t('recommendation.mayTakeFewSeconds')}</span>
                </div>

                {/* Film kareleri animasyonu */}
                {/* <div className="mt-12 flex justify-center space-x-4">
                    {[...Array(5)].map((_, i) => (
                        <div
                            key={i}
                            className="w-12 h-16 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-lg backdrop-blur-sm animate-film-frame"
                            style={{
                                animationDelay: `${i * 0.2}s`
                            }}
                        >
                            <div className="w-full h-full flex items-center justify-center">
                                <Film className="h-6 w-6 text-white/50" />
                            </div>
                        </div>
                    ))}
                </div> */}
            </div>
        </div>
    );

    return (
        <>
            {/* AI Loading Animasyonu - Tam ekran */}
            {(isLoading || isSurpriseLoading) && <AILoadingAnimation />}

            <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                <div className="text-center mb-6 sm:mb-8">
                    <div className="flex justify-center items-center space-x-2 mb-4">
                        <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('recommendation.getRecommendationTitle')}</h1>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 px-4">
                        {t('recommendation.subtitle')}
                    </p>

                    {/* Sürpriz Öner Butonu - Direkt Rastgele Sürpriz! */}
                    <div className="flex justify-center px-4">
                        <button
                            onClick={handleSurpriseMovie}
                            disabled={isSurpriseLoading}
                            className="flex items-center space-x-2 sm:space-x-3 px-4 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 text-white rounded-xl font-medium shadow-lg transform hover:scale-105 transition-all duration-200 text-sm sm:text-base"
                        >
                            {isSurpriseLoading ? (
                                <>
                                    <Loader className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                                    <span>{t('recommendation.surprisePreparing')}</span>
                                </>
                            ) : (
                                <>
                                    <Shuffle className="h-5 w-5 sm:h-6 sm:w-6" />
                                    <span>{t('recommendation.surpriseButton')}</span>
                                </>
                            )}
                        </button>
                    </div>

                    <div className="mt-4 text-center px-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('recommendation.surpriseSubtitle')}
                        </p>
                    </div>

                    {/* Ayırıcı */}
                    <div className="flex items-center my-6 sm:my-8 px-4">
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                        <div className="px-4 text-sm text-gray-500 dark:text-gray-400 font-medium">{t('recommendation.or')}</div>
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8 px-2 sm:px-0">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {/* Tercih Durumu Bilgisi */}
                    {(!user?.preferences?.genres || user.preferences.genres.length === 0) ? (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg">
                            <div className="flex items-start space-x-2">
                                <div className="text-yellow-600 dark:text-yellow-400 mt-0.5">⚠️</div>
                                <div>
                                    <p className="font-medium">{t('recommendation.temporarySelections')}</p>
                                    <p className="text-sm">{t('recommendation.temporarySelectionsDesc')}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300 px-4 py-3 rounded-lg">
                            <div className="flex items-start space-x-2">
                                <div className="text-green-600 dark:text-green-400 mt-0.5">✅</div>
                                <div>
                                    <p className="font-medium">{t('recommendation.permanentPreferences')}</p>
                                    <p className="text-sm">{t('recommendation.permanentPreferencesDesc', { count: user.preferences.genres.length })}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Film/Dizi Seçimi */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t('recommendation.selectContentType')}
                        </h2>
                        <div className="flex gap-3 justify-center mb-4">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, contentType: 'film' }))}
                                className={`px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 flex items-center space-x-2 ${formData.contentType === 'film'
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Film className="h-5 w-5" />
                                <span>{t('recommendation.movie')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, contentType: 'dizi' }))}
                                className={`px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 flex items-center space-x-2 ${formData.contentType === 'dizi'
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Film className="h-5 w-5" />
                                <span>{t('recommendation.series')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Yerli/Yabancı Seçimi */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t('recommendation.selectOrigin')}
                        </h2>
                        <div className="flex gap-3 justify-center mb-4">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, origin: 'yerli' }))}
                                className={`px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 flex items-center space-x-2 ${formData.origin === 'yerli'
                                    ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Home className="h-5 w-5" />
                                <span>{t('recommendation.local')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, origin: 'yabancı' }))}
                                className={`px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 flex items-center space-x-2 ${formData.origin === 'yabancı'
                                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Globe className="h-5 w-5" />
                                <span>{t('recommendation.foreign')}</span>
                            </button>
                        </div>
                    </div>

                    {/* Tür Seçimi */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t('recommendation.favoriteGenres', { type: formData.contentType === 'dizi' ? t('recommendation.series') : t('recommendation.movie') })}
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                            {genres.map((genreObj) => {
                                const isSelected = formData.genres.includes(genreObj.original);
                                return (
                                    <button
                                        key={genreObj.original}
                                        type="button"
                                        onClick={() => handleGenreToggle(genreObj.translated)}
                                        className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${isSelected
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        {genreObj.translated}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Ruh Hali */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {t('recommendation.currentMood')}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                            {moods.map((mood) => {
                                const IconComponent = mood.icon;
                                return (
                                    <button
                                        key={mood.id}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, mood: mood.value }))}
                                        className={`px-3 sm:px-4 py-3 rounded-lg text-xs sm:text-sm font-medium transition-all transform hover:scale-105 flex items-center justify-center space-x-2 ${formData.mood === mood.value
                                            ? 'bg-purple-600 text-white shadow-lg'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                                        <span>{mood.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>


                    {/* Gönder Butonu */}
                    <div className="text-center px-4">
                        <button
                            type="submit"
                            disabled={isLoading || formData.genres.length === 0}
                            onClick={() => {
                                // GA event gönderimi
                                ReactGA.event({
                                    category: "Film Önerisi",
                                    action: "Film önerisi oluşturuldu",
                                    label: "Kullanıcı öneri butonuna bastı",
                                });
                            }}
                            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 flex items-center space-x-2 mx-auto shadow-lg text-sm sm:text-base"
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                                    <span>{t('recommendation.aiPreparing')}</span>
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                                    <span>{t('recommendation.getRecommendations', { type: formData.contentType === 'dizi' ? t('recommendation.series') : t('recommendation.movie') })}</span>
                                </>
                            )}
                        </button>

                        {formData.genres.length === 0 && (
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 px-4">
                                {t('recommendation.selectAtLeastOneGenre')}
                            </p>
                        )}
                    </div>

                </form>

                {/* Sürpriz Film Modal - Direkt sonuç gösteriliyor */}
                {(surpriseMovie || surpriseError) && !isSurpriseLoading && (
                    <SurpriseMovieModal
                        movie={surpriseMovie}
                        error={surpriseError}
                        onClose={closeSurpriseModal}
                    />
                )}

                {/* Trailer Modal */}
                <TrailerModal
                    trailer={selectedTrailer}
                    isOpen={isTrailerModalOpen}
                    onClose={closeTrailerModal}
                    movieTitle={currentMovieForTrailer}
                />

                {/* Confirmation Modal */}
                <ConfirmationModal
                    isOpen={confirmationModal.isOpen}
                    onClose={closeConfirmationModal}
                    onConfirm={handleConfirmationConfirm}
                    title={confirmationModal.title}
                    message={confirmationModal.message}
                    type={confirmationModal.type}
                    confirmText={confirmationModal.confirmText}
                    cancelText={confirmationModal.cancelText}
                    isLoading={confirmationModal.isLoading}
                />

                {/* Toast Notification */}
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        </>
    );
};

export default RecommendationPage; 